import React, { useEffect, useCallback, useState, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faFacebookF } from '@fortawesome/free-brands-svg-icons/faFacebookF'
import { faTwitter } from '@fortawesome/free-brands-svg-icons/faTwitter'
import { faReddit } from '@fortawesome/free-brands-svg-icons/faReddit'
import { faLink } from '@fortawesome/free-solid-svg-icons/faLink'
import { CopyToClipboard } from 'react-copy-to-clipboard'
import { Overlay, Tooltip } from 'react-bootstrap'
import getConfig from 'next/config'
import style from './social-share.module.scss'

const {
  publicRuntimeConfig: {
    baseURL,
    paths: { shareFacebook, shareTwitter, shareReddit },
  },
} = getConfig()

interface Props {
  path: string
}

const SocialShare = ({ path }: Props): JSX.Element => {
  const [copied, setCopied] = useState(false)
  const target = useRef(null)

  useEffect(() => {
    const t = copied ? setTimeout(() => setCopied(false), 1000) : null

    return () => {
      t && clearTimeout(t)
    }
  }, [copied, setCopied])

  const [hashSuffix, setHashSuffix] = useState('')
  useEffect(() => {
    setHashSuffix(window.location.hash)
    const onHashChanged = (e: unknown) => {
      const { newURL } = e as HashChangeEvent
      const url = new URL(newURL)
      setHashSuffix(url.hash)
    }
    window.addEventListener('hashchange', onHashChanged)

    return () => {
      window.removeEventListener('hashchange', onHashChanged)
    }
  }, [])

  const onCopy = useCallback(() => setCopied(true), [setCopied])
  const getShareLink = (shareLink = '', hashEncoded = true) =>
    `${shareLink}${baseURL}/${path}${hashEncoded ? encodeURIComponent(hashSuffix) : hashSuffix}`

  return (
    <div className={style.social}>
      <a href={getShareLink(shareFacebook)} target="_blank" rel="noreferrer">
        <FontAwesomeIcon icon={faFacebookF} />
      </a>
      <a href={getShareLink(shareTwitter)} target="_blank" rel="noreferrer">
        <FontAwesomeIcon icon={faTwitter} />
      </a>
      <a href={getShareLink(shareReddit)} target="_blank" rel="noreferrer">
        <FontAwesomeIcon icon={faReddit} />
      </a>
      <CopyToClipboard text={getShareLink('', false)} onCopy={onCopy}>
        <a
          ref={target}
          href={getShareLink('', false)}
          onClick={(e) => e.preventDefault()}
          rel="nofollow"
        >
          <div>
            <FontAwesomeIcon icon={faLink} className={style.icon} />
            <Overlay target={target.current} show={copied} placement="right">
              {(props) => (
                <Tooltip id="copy-link" {...props}>
                  Copied
                </Tooltip>
              )}
            </Overlay>
          </div>
        </a>
      </CopyToClipboard>
    </div>
  )
}

export default SocialShare
